import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { useUploadTemplateImageMutation } from '@/services/endpoints/templateImages';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const TEMPLATE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Uploads a template image and resolves to its public URL. Rejects with an Error carrying a
 * translated, user-facing message. Type/size are checked here for fast feedback; the server
 * stays authoritative (413/415).
 */
export function useTemplateImageUpload() {
  const workspace = useWorkspace();
  const { t } = useTranslation('campaign');
  const [uploadTemplateImage, { isLoading }] = useUploadTemplateImageMutation();

  const upload = useCallback(
    async (file: File, accept: string[]): Promise<string> => {
      if (!accept.includes(file.type)) throw new Error(String(t('templateImages.unsupportedType')));
      if (file.size > TEMPLATE_IMAGE_MAX_BYTES) throw new Error(String(t('templateImages.tooLarge')));

      try {
        const { url } = await uploadTemplateImage({ workspaceId: workspace.id, file }).unwrap();
        return url;
      } catch (error) {
        const status = (error as { status?: unknown })?.status;
        const key =
          status === 413 ? 'templateImages.tooLarge' : status === 415 ? 'templateImages.unsupportedType' : 'templateImages.uploadFailed';
        throw new Error(String(t(key)));
      }
    },
    [t, uploadTemplateImage, workspace.id]
  );

  return { upload, isUploading: isLoading };
}
